import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import CircularProgress from '@mui/material/CircularProgress'
import TaskHubOverview from './components/TaskHubOverview'
import { PRIVATE_PATHS } from 'utils/paths'
import { selectTaskFilters, selectTaskStats } from 'features/taskManagement/ducks/selectors'
import { setTaskFiltersRoutine } from 'features/taskManagement/ducks/actions'
import { selectActionStats } from 'features/actions/ducks/selectors'
import { selectIncidentStats } from 'features/incidents/ducks'
import { FILTER_RESET as REMEDIALS_FILTER_RESET } from 'features/actions/workspace/remedialsQuery'
import { PRIORITY_LEVELS, priorityKey } from 'features/taskManagement/taskPriority'

const TaskManagement = lazy(() => import('./TaskManagement'))
const RemedialsWorkspace = lazy(() => import('features/actions/workspace/RemedialsWorkspace'))
const IncidentsPage = lazy(() => import('features/incidents/IncidentsPage'))

// This hub models the incident → remedial → task LIFECYCLE, so the tabs read
// temporally: "what just happened" (Incidents) → "what needs fixing"
// (Remedials) → "the work that closes it out" (Tasks). The route key stays
// `actions` (the Action model); the user-facing label is "Remedials".
const TABS = [
  { key: 'overview',   label: 'Overview',   icon: DashboardOutlinedIcon,          tint: '#0f172a' },
  { key: 'incidents',  label: 'Incidents',  icon: ReportProblemOutlinedIcon,      tint: '#9333ea' },
  { key: 'actions',    label: 'Remedials',  icon: BoltOutlinedIcon,               tint: '#D24724' },
  { key: 'tasks',      label: 'Tasks',      icon: AssignmentTurnedInOutlinedIcon, tint: '#33becc' },
]
const VALID_TABS = new Set(TABS.map((t) => t.key))

/**
 * Tasks & Actions hub. Three previously-separate pages (Tasks, Actions,
 * Incidents) plus a shared Overview live behind one set of tabs so the
 * relationship between them is visible and switching is one click.
 *
 * Stats are fetched once at the hub level so the tab badges, the overview
 * cards, and the embedded pages all read from the same Redux slices —
 * no double-fetching, and the badge counts stay in sync as the user works.
 */
const TASK_FILTER_PARAMS = ['status', 'domain', 'overdue', 'completed_late', 'assignee', 'bp', 'portfolio', 'estate', 'open', 'approver', 'deadline_from', 'deadline_to', 'origin', 'priority']
const TASK_ONLY_PARAMS = TASK_FILTER_PARAMS.filter(p => !['status', 'overdue', 'bp', 'portfolio', 'estate'].includes(p))
const REMEDIALS_ONLY_PARAMS = ['view', 'sort', ...Object.keys(REMEDIALS_FILTER_RESET)]

const TaskHub = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const taskStats = useSelector(selectTaskStats)
  const actionStats = useSelector(selectActionStats)
  const incidentStats = useSelector(selectIncidentStats)

  // Stats are fetched lazily by each consumer (TaskHubOverview for the
  // dashboard, ActionsPage / IncidentsPage / TaskManagement for their own
  // pages). The hub used to pre-fetch all three on mount, but since each
  // child also re-fetches with its own filters that produced 2× duplicate
  // calls per tab. Tab badges show whatever's already in the store —
  // populated by the most recently-visited surface.
  // (Intentionally no useEffect here — see comment above.)

  // Active tab is derived from the URL — pathname first, then ?tab= as a
  // fallback for /tasks which can show either Overview or the Tasks list.
  // This lets the same hub render at /tasks, /actions, and /incidents
  // without each route needing its own wrapper.
  const requestedTab = searchParams.get('tab')
  const hasTaskFilterParam = TASK_FILTER_PARAMS.some((p) => searchParams.get(p))
  const activeTab = useMemo(() => {
    if (location.pathname.startsWith(PRIVATE_PATHS.actions)) return 'actions'
    if (location.pathname.startsWith(PRIVATE_PATHS.incidents)) return 'incidents'
    if (requestedTab && VALID_TABS.has(requestedTab)) return requestedTab
    if (hasTaskFilterParam) return 'tasks'
    return 'overview'
  }, [location.pathname, requestedTab, hasTaskFilterParam])

  // Tab → URL mapping. Each tab has a canonical path so the URL stays
  // shareable and the deep-links the rest of the app generates
  // (`/actions?incident=…`) keep working.
  const taskFilters = useSelector(selectTaskFilters)
  const setTab = useCallback((nextTab) => {
    const onTasks = location.pathname.startsWith(PRIVATE_PATHS.taskManagement) && searchParams.get('tab') === 'tasks'
    const scope = onTasks
      ? { bp: taskFilters?.buildingId, portfolio: taskFilters?.portfolioId, estate: taskFilters?.estateId }
      : { bp: searchParams.get('bp'), portfolio: searchParams.get('portfolio'), estate: searchParams.get('estate') }
    const from = searchParams.get('from')
    const query = extra => {
      const q = new URLSearchParams(extra)
      if (from) q.set('from', from)
      const text = q.toString()
      return text ? `?${text}` : ''
    }
    const scoped = Object.fromEntries(Object.entries(scope).filter(([, v]) => v))

    if (nextTab === 'overview') {
      navigate(`${PRIVATE_PATHS.taskManagement}${query({})}`)
    } else if (nextTab === 'tasks') {
      const differs = (taskFilters?.buildingId || null) !== (scope.bp || null)
        || (taskFilters?.portfolioId || null) !== (scope.portfolio || null)
        || (taskFilters?.estateId || null) !== (scope.estate || null)
      if (differs) {
        dispatch(setTaskFiltersRoutine({ buildingId: scope.bp || null, portfolioId: scope.portfolio || null, estateId: scope.estate || null }))
      }
      navigate(`${PRIVATE_PATHS.taskManagement}${query({ tab: 'tasks' })}`)
    } else if (nextTab === 'actions') {
      navigate(`${PRIVATE_PATHS.actions}${query(scoped)}`)
    } else if (nextTab === 'incidents') {
      navigate(`${PRIVATE_PATHS.incidents}${query(scoped)}`)
    }
  }, [navigate, dispatch, location.pathname, searchParams, taskFilters])

  const handleNavigate = useCallback((tabKey) => {
    if (VALID_TABS.has(tabKey)) {
      setTab(tabKey)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      navigate(tabKey)
    }
  }, [setTab, navigate])

  const prevTabRef = useRef(activeTab)
  useEffect(() => {
    const prevTab = prevTabRef.current
    prevTabRef.current = activeTab
    if (prevTab === activeTab) return
    const leaving = prevTab === 'actions' ? REMEDIALS_ONLY_PARAMS
      : prevTab === 'tasks' ? TASK_ONLY_PARAMS
      : null
    if (!leaving) return
    setSearchParams(prev => {
      if (!leaving.some(p => prev.has(p))) return prev
      const next = new URLSearchParams(prev)
      leaving.forEach(p => next.delete(p))
      return next
    }, { replace: true })
  }, [activeTab, setSearchParams])

  // Tab badge counts. Null means "don't show a badge yet" (stats still
  // loading or zero). We deliberately don't show 0 — empty surfaces don't
  // need to advertise themselves.
  const badges = useMemo(() => ({
    overview: null,
    tasks:     (taskStats?.openTasks ?? taskStats?.total ?? 0) || null,
    actions:   (actionStats?.views?.allOpen ?? actionStats?.byStatus?.open ?? 0) || null,
    incidents: (incidentStats?.byStatus?.open ?? 0) || null,
  }), [taskStats, actionStats, incidentStats])

  // Deep-link filter seeding. Dashboard widgets (and any shared link) can land
  // the user on a pre-filtered Tasks workboard via `?status=`, `?overdue=`,
  // `?domain=` or `?assignee=`. We translate those into the task filter slice
  // (the same mechanism the in-page filter bar uses) and then normalise the URL
  // to the canonical `?tab=tasks` workboard so a refresh keeps the filter and
  // the client-side category doesn't re-narrow the server-filtered list.
  const statusParam = searchParams.get('status')
  const domainParam = searchParams.get('domain')
  const overdueParam = searchParams.get('overdue')
  const completedLateParam = searchParams.get('completed_late')
  const assigneeParam = searchParams.get('assignee')
  const bpParam = searchParams.get('bp')
  const portfolioParam = searchParams.get('portfolio')
  const estateParam = searchParams.get('estate')
  const openParam = searchParams.get('open')
  const approverParam = searchParams.get('approver')
  const deadlineFromParam = searchParams.get('deadline_from')
  const deadlineToParam = searchParams.get('deadline_to')
  const originParam = searchParams.get('origin')
  const priorityParam = searchParams.get('priority')
  const returnBp = searchParams.get('from')
  useEffect(() => {
    if (location.pathname.startsWith(PRIVATE_PATHS.actions)) return
    if (location.pathname.startsWith(PRIVATE_PATHS.incidents)) return
    if (!statusParam && !domainParam && !overdueParam && !completedLateParam && !assigneeParam
      && !bpParam && !portfolioParam && !estateParam
      && !openParam && !approverParam && !deadlineFromParam && !deadlineToParam && !originParam && !priorityParam) return

    const patch = {
      status: 'all',
      taskDomainId: null,
      assignedTo: null,
      approverId: null,
      open: false,
      completedLate: false,
      buildingId: null,
      portfolioId: null,
      estateId: null,
      deadlineFrom: null,
      deadlineTo: null,
      origin: null,
      priority: null,
    }
    const isOverdue = statusParam === 'overdue'
      || overdueParam === '1' || overdueParam === 'true'
    if (isOverdue) patch.status = 'overdue'
    else if (statusParam) patch.status = statusParam
    if (domainParam) patch.taskDomainId = domainParam
    if (assigneeParam) patch.assignedTo = assigneeParam
    if (approverParam) patch.approverId = approverParam
    if (openParam === '1' || openParam === 'true') patch.open = true
    if (completedLateParam === '1' || completedLateParam === 'true') patch.completedLate = true
    if (bpParam) patch.buildingId = bpParam
    if (portfolioParam) patch.portfolioId = portfolioParam
    if (estateParam) patch.estateId = estateParam
    if (deadlineFromParam) patch.deadlineFrom = deadlineFromParam
    if (deadlineToParam) patch.deadlineTo = deadlineToParam
    if (['corrective', 'obligation', 'standard'].includes(originParam)) patch.origin = originParam
    if (PRIORITY_LEVELS.includes(priorityKey(priorityParam))) patch.priority = priorityKey(priorityParam)

    dispatch(setTaskFiltersRoutine(patch))

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      TASK_FILTER_PARAMS.forEach((p) => next.delete(p))
      next.set('tab', 'tasks')
      next.set('category', 'all_tasks')
      return next
    }, { replace: true })
  }, [
    dispatch, setSearchParams, location.pathname,
    statusParam, domainParam, overdueParam, completedLateParam, assigneeParam,
    bpParam, portfolioParam, estateParam,
    openParam, approverParam, deadlineFromParam, deadlineToParam, originParam, priorityParam,
  ])

  return (
    <HubWrapper>
      <PageHead>
        {returnBp && (
          <BackLink type='button' onClick={() => navigate(`/bp-list/${returnBp}`)}>
            ← Back to Building Passport
          </BackLink>
        )}
        <PageTitle>Tasks &amp; Remedials</PageTitle>
      </PageHead>

      <TabBarOuter>
        <TabBar role="tablist" aria-label="Task Management sections">
          {TABS.map((t) => {
            const Icon = t.icon
            const active = activeTab === t.key
            const badge = badges[t.key]
            return (
              <Tab
                key={t.key}
                role="tab"
                aria-selected={active}
                $active={active}
                $tint={t.tint}
                onClick={() => setTab(t.key)}
                type="button"
              >
                <Icon style={{ fontSize: 18 }} />
                <TabLabel>{t.label}</TabLabel>
                {badge && (
                  <TabBadge $active={active} $tint={t.tint}>
                    {badge > 99 ? '99+' : badge}
                  </TabBadge>
                )}
              </Tab>
            )
          })}
        </TabBar>

        <CalendarLink
          type="button"
          onClick={() => navigate(PRIVATE_PATHS.taskDomains)}
          aria-label="Manage task domains"
        >
          <LabelOutlinedIcon style={{ fontSize: 16 }} />
          <span>Manage domains</span>
        </CalendarLink>

        <CalendarLink
          type="button"
          onClick={() => navigate(PRIVATE_PATHS.calendar)}
          aria-label="Open calendar"
        >
          <CalendarMonthOutlinedIcon style={{ fontSize: 16 }} />
          <span>Calendar</span>
        </CalendarLink>
      </TabBarOuter>

      <TabPanel role="tabpanel">
        {activeTab === 'overview' && <TaskHubOverview onNavigate={handleNavigate} />}
        {activeTab !== 'overview' && (
          <Suspense fallback={<LazyFallback><CircularProgress size={20} sx={{ color: '#D24724' }} /></LazyFallback>}>
            {activeTab === 'tasks' && <TaskManagement embedded />}
            {activeTab === 'actions' && <RemedialsWorkspace scope={{ kind: 'all' }} />}
            {activeTab === 'incidents' && <IncidentsPage />}
          </Suspense>
        )}
      </TabPanel>
    </HubWrapper>
  )
}

export default TaskHub

const HubWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const PageHead = styled.div`
  padding: 4px 4px 0 4px;
  position: relative;
  z-index: 7;
  @media (max-width: 768px) { padding: 0 4px; }
`

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  padding: 0 0 2px 0;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  &:hover { color: #0f172a; }
`

const PageTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  @media (max-width: 768px) { font-size: 16px; }
`

const PageSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
  @media (max-width: 768px) { font-size: 12px; }
`

const TabBarOuter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  position: sticky;
  top: 96px;
  z-index: 6;

  &::before {
    content: '';
    position: absolute;
    left: -4px;
    right: -4px;
    top: -60px;
    bottom: 100%;
    background: rgb(247, 248, 253);
    z-index: -1;
  }

  @media (max-width: 768px) {
    position: static;
    flex-wrap: wrap;
    padding: 6px;
    gap: 6px;

    &::before { display: none; }
  }
`

const TabBar = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }

  /* The tabs scroll on mobile; fade the trailing edge so a clipped tab reads
     as "there is more here" rather than as a broken layout. */
  @media (max-width: 768px) {
    flex: 1 1 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;
    overflow: visible;
  }
`

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  color: ${({ $active, $tint }) => ($active ? $tint : '#475569')};
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
  position: relative;

  &:hover {
    background: ${({ $active }) => ($active ? '#fff' : '#f1f5f9')};
    color: ${({ $active, $tint }) => ($active ? $tint : '#0f172a')};
  }

  /* Active underline — keeps the pill compact while still signalling
     the active tab clearly on light backgrounds. */
  ${({ $active, $tint }) => $active && `
    &::after {
      content: '';
      position: absolute;
      left: 14px;
      right: 14px;
      bottom: 4px;
      height: 2px;
      background: ${$tint};
      border-radius: 2px;
    }
  `}

  @media (max-width: 768px) {
    justify-content: center;
    min-height: 44px;
    padding: 8px 10px;
    font-size: 14px;
    background: ${({ $active }) => ($active ? '#f1f5f9' : 'transparent')};
  }
`

const TabLabel = styled.span``

const TabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  background: ${({ $active, $tint }) => ($active ? $tint : '#f1f5f9')};
  color: ${({ $active }) => ($active ? '#fff' : '#475569')};
`

const CalendarLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: #f8fafc; color: #0f172a; }

  @media (max-width: 768px) {
    flex: 1 1 0;
    justify-content: center;
    min-height: 40px;
    font-size: 13px;
  }
`

const TabPanel = styled.div`
  min-height: 50vh;
`

const LazyFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
`
